import { useCallback } from "react";
import { openRunStream, readFrames, type ChatCallError } from "@/features/intelligence/api/client";
import {
  abandonTurn,
  applyFrame,
  endAllCalls,
  loseStream,
  runGoneUpstream,
  shouldReattach,
  type DocumentReadBack,
  type ThreadState,
} from "@/features/intelligence/lib/thread-state";
import type { FrontendToolRunner } from "@/features/intelligence/lib/frontend-tool-run";
import { forgetRun, rememberRun, sleep, toolCallEndId } from "./runtime";
import type { ApplyThread, DriveStream, MutableRef, OpenStream, StateSetter } from "./types";

export function useStreamExecution({
  activeIdRef,
  abortRef,
  busyRef,
  threadRef,
  runnerRef,
  setBusy,
  setCanReconnect,
  setActionError,
  applyThread,
  healAfterStream,
  runFrontendTool,
}: {
  activeIdRef: MutableRef<string | null>;
  abortRef: MutableRef<AbortController | null>;
  busyRef: MutableRef<boolean>;
  threadRef: MutableRef<ThreadState>;
  runnerRef: MutableRef<FrontendToolRunner | null>;
  setBusy: StateSetter<boolean>;
  setCanReconnect: StateSetter<boolean>;
  setActionError: StateSetter<ChatCallError | null>;
  applyThread: ApplyThread;
  healAfterStream: (id: string, runId: string | null) => Promise<DocumentReadBack | null>;
  runFrontendTool: (id: string, runId: string, toolCallId: string) => Promise<void>;
}): DriveStream {
  return useCallback(
    async (id: string, open: OpenStream, options: { quiet404?: boolean } = {}) => {
      const controller = new AbortController();
      abortRef.current = controller;
      busyRef.current = true;
      setBusy(true);
      setCanReconnect(false);
      try {
        let opened = await open(controller.signal);
        if (!opened.ok) {
          if (!(options.quiet404 && opened.error.status === 404)) {
            setActionError(opened.error);
          }
          const runGone = opened.error.status === 404;
          applyThread((state) => (runGone ? endAllCalls(abandonTurn(state)) : abandonTurn(state)));
          return;
        }
        let rememberedRun: string | null = null;
        for (let hop = 0; ; ) {
          await readFrames(opened.value, (frame) => {
            const before = threadRef.current.runError;
            applyThread((state) => applyFrame(state, frame));
            const after = threadRef.current.runError;
            if (after && after !== before) {
              console.warn(
                `[simon] RUN_ERROR code=${after.code ?? "(none)"} ` +
                  `correlationId=${after.correlationId ?? "(none)"} ` +
                  `message=${after.message ?? "(none)"}`,
              );
            }
            const runId = threadRef.current.runId;
            if (runId && runId !== rememberedRun) {
              rememberedRun = runId;
              rememberRun(id, runId);
            }
            const endedId = toolCallEndId(frame);
            if (endedId && runnerRef.current && runId) {
              void runFrontendTool(id, runId, endedId);
            }
          });
          if (controller.signal.aborted) return;
          const state = threadRef.current;
          if (!shouldReattach(state) || !state.runId) {
            if (state.outcome !== "running" && state.outcome !== "interrupted") forgetRun(id);
            break;
          }
          if (hop >= MAX_CLIENT_HOPS) {
            setCanReconnect(true);
            break;
          }
          hop += 1;
          await sleep(HOP_DELAY_MS * hop);
          if (controller.signal.aborted) return;
          const next = await openRunStream(id, state.runId, state.lastEventId, controller.signal);
          if (!next.ok) {
            if (next.error.status !== 404) setCanReconnect(true);
            else applyThread(runGoneUpstream);
            break;
          }
          opened = next;
        }
      } finally {
        if (abortRef.current === controller || abortRef.current === null) {
          busyRef.current = false;
          setBusy(false);
          abortRef.current = null;
        }
      }

      const turnRunId = threadRef.current.runId;
      const seen = await healAfterStream(id, turnRunId);
      if (seen === null && activeIdRef.current === id && !busyRef.current) {
        const stranded = threadRef.current;
        if (stranded.outcome === "running") {
          applyThread(loseStream);
          if (stranded.runId) setCanReconnect(true);
        }
      }
    },
    [
      abortRef,
      activeIdRef,
      applyThread,
      busyRef,
      healAfterStream,
      runFrontendTool,
      runnerRef,
      setActionError,
      setBusy,
      setCanReconnect,
      threadRef,
    ],
  );
}

const MAX_CLIENT_HOPS = 3;
const HOP_DELAY_MS = 2000;
